import React from 'react'
import { DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Button, DatePicker, Modal, Radio, Tooltip } from 'antd'
import { AxiosError, AxiosResponse } from 'axios'
import dayjs, { Dayjs } from 'dayjs'
import { sellerApi } from 'api/seller'
import { showServerError } from 'shared/utils/showServerError'
import 'dayjs/locale/ru'
import styles from './SellersReportModal.module.scss'

type TReportType = 'standard' | 'extended'
type TDateRange = [Dayjs, Dayjs]

// Раньше этой даты логов запросов нет, история активности начинается с неё
const ACTIVITY_HISTORY_START = dayjs('2026-06-28')
const DAY_FORMAT = 'YYYY-MM-DD'

const defaultRange = (): TDateRange => {
  const from = dayjs().subtract(29, 'day')
  return [from.isBefore(ACTIVITY_HISTORY_START) ? ACTIVITY_HISTORY_START : from, dayjs()]
}

const saveFile = (response: AxiosResponse<Blob>, fileName: string) => {
  const url = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

// Ответ ждали файлом, поэтому и текст ошибки бэкенда приходит Blob'ом
const showBlobError = async (e: unknown) => {
  const response = (e as AxiosError).response
  if (response?.data instanceof Blob) {
    try {
      response.data = JSON.parse(await response.data.text())
    } catch {
      response.data = undefined
    }
  }
  showServerError(e)
}

interface ISellersReportModalProps {
  open: boolean
  onClose: () => void
}

export const SellersReportModal: React.FC<ISellersReportModalProps> = ({ open, onClose }) => {
  const [type, setType] = React.useState<TReportType>('standard')
  const [range, setRange] = React.useState<TDateRange>(defaultRange)
  const [isDownloading, setIsDownloading] = React.useState(false)

  const onDownload = async () => {
    try {
      setIsDownloading(true)
      if (type === 'standard') {
        saveFile(await sellerApi.exportSellersReport(), 'sellers_report.xlsx')
      } else {
        const dateFrom = range[0].format(DAY_FORMAT)
        const dateTo = range[1].format(DAY_FORMAT)
        saveFile(
          await sellerApi.exportSellersActivityReport(dateFrom, dateTo),
          `sellers_activity_${dateFrom}_${dateTo}.xlsx`
        )
      }
      onClose()
    } catch (e) {
      await showBlobError(e)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Modal
      centered
      open={open}
      title='Отчёт по продавцам'
      onCancel={onClose}
      width={420}
      footer={
        <Button
          size='large'
          icon={<DownloadOutlined />}
          loading={isDownloading}
          onClick={onDownload}
          className={styles.downloadButton}
        >
          Скачать
        </Button>
      }
    >
      <Radio.Group
        value={type}
        onChange={(e) => setType(e.target.value)}
        className={styles.types}
      >
        <Radio value='standard'>
          Стандартный{' '}
          <Tooltip title='Все продавцы на сегодня: контакты, статус, сколько товаров в каждом статусе, когда подтверждали остатки и синхронизировался МойСклад.'>
            <QuestionCircleOutlined className={styles.hint} />
          </Tooltip>
        </Radio>
        <Radio value='extended'>
          Расширенный{' '}
          <Tooltip title='Активность продавцов за период: кто вернулся после долгой паузы, кто снова начал обновлять остатки, новые продавцы и история заходов в кабинет и обновлений остатков по дням.'>
            <QuestionCircleOutlined className={styles.hint} />
          </Tooltip>
        </Radio>
      </Radio.Group>
      {type === 'extended' && (
        <DatePicker.RangePicker
          value={range}
          format='DD.MM.YYYY'
          onChange={(value) => {
            if (value?.[0] && value[1]) setRange([value[0], value[1]])
          }}
          disabledDate={(date) =>
            date.isBefore(ACTIVITY_HISTORY_START, 'day') || date.isAfter(dayjs(), 'day')
          }
          allowClear={false}
          inputReadOnly
          className={styles.range}
        />
      )}
    </Modal>
  )
}
